import React from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { Link } from "react-router-dom";
import { ReactComponent as ChevronDownSvg } from "../../../assets/Icons/IconChevron-Down.svg";
import { useChangelogNewItemIds } from "@shared/changelog/ChangelogNewItemsContext";
import { MenuItem as MenuItemType } from "../MenuData";
import {
  BADGE_DOT,
  BADGE_INLINE,
  BADGE_OVERLAY,
  CHEVRON_ICON,
  FOCUS_RING,
  LABEL_CLAMP,
  NESTED_INDENT,
  ROW_GAP,
  ROW_ICON,
  ROW_ICON_STANDALONE,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_STACK,
  ROW_TEXT,
  TRANSITION_COLORS,
  TRANSITION_TRANSFORM,
} from "../sideMenuDensity";

interface MenuItemProps {
  item: MenuItemType;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  activeItem: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  onClick: (item: MenuItemType) => void;
  isExpanded?: boolean;
}

/**
 * Whether this row, or anything under it, is tagged `New` (CTX-511).
 *
 * The descendant half is what keeps a `New` from hiding inside a closed
 * section: children render only when the rail is expanded *and* the section is
 * open, so in every other state the parent is the only thing on screen that can
 * carry the mark.
 */
function hasNewDescendant(item: MenuItemType, newItemIds: Set<string>): boolean {
  if (!item.children) return false;
  return item.children.some(
    (child) =>
      (child.id ? newItemIds.has(child.id) : false) ||
      hasNewDescendant(child, newItemIds)
  );
}

/**
 * The one mark the collapsed rail may show, or `null`.
 *
 * Collapsed, every mark overlays the same 15px glyph, and the three used to
 * render in independent branches — a row could carry a `Beta` dot, a `New` dot
 * and a count at once, wrapped into the corner. At 6px two adjacent dots are
 * told apart by fill alone, so the stack did not read as three facts; it read as
 * smudge.
 *
 * The order is how fast each mark goes stale. A count is minutes old and is the
 * only one that can be acted on right now; a `New` expires on its own in
 * `NEW_TAG_MAX_AGE_DAYS`; `Beta` is a product state with no end date, so it is
 * the one that loses nothing by waiting for the rail to be expanded — where all
 * of them are still shown side by side, which stays deliberate (CTX-502).
 */
type CollapsedMark = "count" | "new" | "beta";

function collapsedMarkFor(
  item: MenuItemType,
  showsNewDot: boolean
): CollapsedMark | null {
  if (item.badge) return "count";
  if (showsNewDot) return "new";
  if (item.badgeBeta) return "beta";
  return null;
}

function isActiveOrChildActive(
  item: MenuItemType,
  activeItem: string
): boolean {
  // Use item.id if available, otherwise fall back to label for backward compatibility
  const itemIdentifier = item.id || item.label;
  if (itemIdentifier === activeItem) return true;
  if (item.children) {
    for (const child of item.children) {
      if (isActiveOrChildActive(child, activeItem)) {
        return true;
      }
    }
  }
  return false;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  expandedSections,
  setExpandedSections,
  activeItem,
  setActiveItem,
  onClick,
  isExpanded = true,
}) => {
  const isParent = !!item.children;
  const isSectionExpanded = expandedSections[item.label] || false;
  const newItemIds = useChangelogNewItemIds();
  const isNew = item.id ? newItemIds.has(item.id) : false;
  // Children are mounted only when both are true, so anywhere else their marks
  // have to surface on this row or not at all.
  const areChildrenVisible = isExpanded && isSectionExpanded;
  const hasHiddenNewDescendant =
    !areChildrenVisible && hasNewDescendant(item, newItemIds);
  /**
   * A row that *is* new gets the word; a section merely containing one gets a
   * dot. The distinction is the honest one — a pill on "Management" would claim
   * the section changed, when what changed is one screen inside it — and it is
   * also why the dot is not just the collapsed rail's shrunken pill.
   */
  const showsNewPill = isExpanded && isNew;
  const showsNewDot = !showsNewPill && (isNew || hasHiddenNewDescendant);
  const collapsedMark = collapsedMarkFor(item, showsNewDot);
  const activeOrChildActive = isActiveOrChildActive(item, activeItem);
  // Use item.id if available, otherwise fall back to label for backward compatibility
  const itemIdentifier = item.id || item.label;
  const isSelfActive = itemIdentifier === activeItem;
  const isSomeChildActive = !isSelfActive && activeOrChildActive;

  const handleItemClick = (e: React.MouseEvent) => {
    if (isParent) {
      e.preventDefault();
      setExpandedSections((prev) => ({
        ...prev,
        [item.label]: !isSectionExpanded,
      }));
      // Use item.id if available, otherwise fall back to label for backward compatibility
      setActiveItem(item.id || item.label);
    } else {
      // Use item.id if available, otherwise fall back to label for backward compatibility
      setActiveItem(item.id || item.label);
      onClick(item);
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSections((prev) => ({
      ...prev,
      [item.label]: !isSectionExpanded,
    }));
    // Use item.id if available, otherwise fall back to label for backward compatibility
    setActiveItem(item.id || item.label);
  };

  return (
    <li className={ROW_TEXT}>
      <div className="flex justify-between items-center">
        {/* 
          Si es padre, el link NO navega (o apunta a "#"). 
          Si no es padre (no tiene children), usamos item.path para navegar. 
        */}
        <Link
          to={isParent ? "#" : item.path || "#"}
          /* A leaf is a place that can be kept open as a view (project 13D): right click
             "Add view", Cmd/Ctrl+click or middle click. Sections are not places. */
          {...viewHref(isParent ? null : item.path)}
          onClick={handleItemClick}
          /* The active fill is `state-active-brand` (the accent tint), not the
             `state-active` that hover uses: with both on the neutral fill the
             selected row and the row under the cursor were indistinguishable,
             so the rail could not answer "where am I" while the mouse was in it.
             Both tokens already carry their own light/dark values, which is what
             makes the accent legible in dark without a second definition here. */
          className={`
            block w-full ${ROW_PADDING} ${ROW_RADIUS} ${TRANSITION_COLORS} ${FOCUS_RING}
            ${
              isSelfActive
                ? "bg-semantic-state-active-brand text-semantic-text-brand font-semibold"
                : isSomeChildActive
                ? "text-semantic-text-brand font-medium"
                : "hover:bg-semantic-state-active text-semantic-text-secondary"
            }
          `}
        >
          <div className={`flex items-center ${ROW_GAP}`}>
            {item.Icon && (
              <span className="relative inline-flex shrink-0">
                {/* Expanded, the icon stays inside the label's line box (13px);
                    collapsed, it is the row's only content and takes the
                    standalone size (15px). */}
                <item.Icon
                  className={isExpanded ? ROW_ICON : ROW_ICON_STANDALONE}
                />
                {/* Collapsed rail: one mark overlays the standalone glyph — which
                    one is `collapsedMarkFor`'s call. A static word becomes a dot
                    (BADGE_DOT) while a count keeps its digits (BADGE_OVERLAY).
                    Both are aria-hidden — the expanded rail is where the label is
                    announced. */}
                {!isExpanded && collapsedMark && (
                  <span className="absolute -right-1 -top-1 flex items-center justify-end">
                    {collapsedMark === "count" && item.badge && (
                      <span
                        className={
                          /^\d+\+?$/.test(item.badge)
                            ? `${BADGE_OVERLAY} bg-red-600 text-white shadow-sm ring-1 ring-red-700/50 animate-pulse`
                            : `${BADGE_OVERLAY} bg-semantic-bg-danger-secondary text-semantic-text-danger`
                        }
                        aria-hidden
                      >
                        {item.badge}
                      </span>
                    )}
                    {/* Solid `brand-bold` against Beta's tinted `brand-secondary`.
                        The two no longer appear together here, but the weights
                        stay distinct because the same row can show either one
                        across sessions, and `data-testid` is the only handle a
                        test has on a dot whose word is aria-hidden. */}
                    {collapsedMark === "new" && (
                      <span
                        className={`${BADGE_DOT} bg-semantic-bg-brand-bold ring-1 ring-semantic-border-secondary`}
                        data-testid="menu-item-new-dot"
                        aria-hidden
                      />
                    )}
                    {collapsedMark === "beta" && (
                      <span
                        className={`${BADGE_DOT} bg-semantic-bg-brand-secondary ring-1 ring-semantic-border-secondary`}
                        data-testid="menu-item-beta-dot"
                        aria-hidden
                      />
                    )}
                  </span>
                )}
              </span>
            )}
            {isExpanded && (
              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-0.5">
                  <span
                    className={
                      item.badgeBeta || showsNewPill || showsNewDot
                        ? `${LABEL_CLAMP} mr-1.5`
                        : LABEL_CLAMP
                    }
                  >
                    {item.label}
                  </span>
                  {item.badgeBeta && (
                    <span
                      className={`${BADGE_INLINE} bg-semantic-bg-brand-secondary text-semantic-text-primary`}
                    >
                      {item.badgeBeta}
                    </span>
                  )}
                  {/* A row can wear both marks — `Beta` says how finished the
                      screen is, `New` says whether you have seen it — and the
                      flex container above already handles two. */}
                  {showsNewPill && (
                    <span
                      className={`${BADGE_INLINE} bg-semantic-bg-brand-bold text-semantic-text-inverse-primary`}
                      data-testid="menu-item-new-pill"
                    >
                      New
                    </span>
                  )}
                  {/* A closed section, on the wide rail: the child carrying the
                      tag is not mounted, so this is the only thing standing
                      between a `New` and being invisible until someone happens
                      to open the section. */}
                  {showsNewDot && (
                    <span
                      className={`${BADGE_DOT} bg-semantic-bg-brand-bold shrink-0`}
                      data-testid="menu-item-new-dot"
                      aria-label={`${item.label} has new items`}
                      role="img"
                    />
                  )}
                </span>
                {item.badge && (
                  <span
                    className={
                      /^\d+\+?$/.test(item.badge)
                        ? `${BADGE_INLINE} bg-red-600 text-white shadow-sm ring-1 ring-red-700/50 animate-pulse`
                        : `${BADGE_INLINE} bg-semantic-bg-danger-secondary text-semantic-text-danger`
                    }
                  >
                    {item.badge}
                  </span>
                )}
              </span>
            )}
          </div>
        </Link>

        {/* 
          Botón flecha para expandir/cerrar
          Aparece sólo si es padre y el menú está expandido 
        */}
        {isParent && isExpanded && (
          <button
            onClick={handleChevronClick}
            aria-label={isSectionExpanded ? "Collapse section" : "Expand section"}
            /* Explicit square ≥ the row height: the shorter rows left a bare
               `px-2` chevron with a hit area smaller than the row it belongs to.

               This carried `focus:outline-none` with nothing in its place, so a
               keyboard user tabbing through a collapsible section had no idea
               where they were. `FOCUS_RING` scopes the suppression to
               `focus-visible` and puts a ring back. */
            className={`flex h-7 w-7 shrink-0 items-center justify-center ${ROW_RADIUS} text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`}
          >
            <ChevronDownSvg
              className={`${CHEVRON_ICON} ${TRANSITION_TRANSFORM} ${
                isSectionExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </div>

      {/* Submenú */}
      {isExpanded && isSectionExpanded && item.children && (
        <ul className={`${NESTED_INDENT} ${ROW_STACK}`}>
          {item.children.map((child) => (
            <MenuItem
              key={child.label}
              item={child}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              onClick={onClick}
              isExpanded={isExpanded}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default MenuItem;
